import React, { Component } from "react";
import { AutoBind } from "../../rule-engine-core/Common/AutoBind";
import { Sidebar, SidebarItem } from "../../CommonUI/BuilderUI/Sidebar";
import { Topbar } from "../../CommonUI/BuilderUI/Topbar";
import { BuilderEditPage } from "../../CommonUI/BuilderUI/BuilderEditPage";

import { CodingIcon } from "../../CommonUI/Icon/CodingIcon";
import { DataIcon } from "../../CommonUI/Icon/DataIcon";
import { RuleTableIcon } from "../../CommonUI/Icon/RuleTableIcon";

import { TestDataIcon } from "../../CommonUI/Icon/TestDataIcon";
import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { XExecutableTester } from "../../CommonUI/Testing/XExecutableTester";
import { RuleEditorDiv } from "../DesignUI/CommonStyles";
import { TreeRuleDesigner } from "./Builder/TreeRuleDesigner";
import { TreeRuleConfiguration } from "../../rule-engine-core/Rule/Tree/TreeRuleConfiguration";
import { TreeRuleEditContext } from "../../rule-engine-core/Rule/Tree/TreeRuleEditContext";
import { RuleBuilderPage } from "../../rule-engine-core/Rule/RuleBuilderPage";
import { DataBuilder } from "../../DataBuilder/DataBuilder";
import { UnitTestConfiguration } from "../../rule-engine-core/Common/UnitTesting/UnitTestConfiguration";

export interface ITreeRuleEditor {
    ruleDefinition: TreeRuleConfiguration;
    treeRuleName?: string;
    onTreeRuleNameUpdate?: (newName: string) => void;
    onRuleSave?: () => void;
    onRuleCancel?: () => void;
    saveUnitTest?: (unitTest: UnitTestConfiguration) => void;
    getUnitTests?: () => Promise<UnitTestConfiguration[]>;
}

interface ITreeRuleState {
    showColor: boolean;
}

export class TreeRuleBuilder extends Component<ITreeRuleEditor, ITreeRuleState> {
    editContext: TreeRuleEditContext;
    tabs: SidebarItem[];
    ruleDefinition: TreeRuleConfiguration;

    constructor(props: ITreeRuleEditor) {
        super(props);

        this.ruleDefinition = props.ruleDefinition;
        this.editContext = new TreeRuleEditContext(props.ruleDefinition);

        if (props.treeRuleName) {
            this.editContext.title = props.treeRuleName;
        }

        if (props.onRuleSave) {
            this.editContext.onRuleSave = props.onRuleSave;
        }

        if (props.onRuleCancel) {
            this.editContext.onRuleCancel = props.onRuleCancel;
        }

        this.tabs = this.getDefaultTabs();

        this.state = { showColor: false };
    }

    @AutoBind
    onChangeRuleName(newName: string) {
        this.editContext.title = newName;
        StateManager.propertyChanged(this.editContext, "title");
        if (this.props.onTreeRuleNameUpdate) this.props.onTreeRuleNameUpdate(newName);
    }

    getDefaultTabs(): SidebarItem[] {
        return [
            {
                value: RuleBuilderPage.Rule,
                renderUI: () => (
                    <TreeRuleDesigner
                        editContext={this.editContext}
                        ruleDefinition={this.ruleDefinition}
                        treeRuleBuilder={this}
                    />
                ),
                icon: <RuleTableIcon />,
            },
            {
                value: RuleBuilderPage.Data,
                renderUI: () => (
                    <DataBuilder
                        definition={this.props.ruleDefinition.getInputDefinition()}
                        readOnly={!this.props.ruleDefinition.canChangeInputs}
                    />
                ),
                icon: <DataIcon />,
            },
            {
                value: RuleBuilderPage.Configuration,
                renderUI: () => (
                    <BuilderEditPage
                        builder={this.props.ruleDefinition}
                        updateCallback={() => {}}
                    />
                ),
                icon: <CodingIcon />,
            },
            {
                value: RuleBuilderPage.Test,
                renderUI: () => (
                    <XExecutableTester
                        saveUnitTest={this.props.saveUnitTest}
                        executable={this.props.ruleDefinition}
                    />
                ),
                icon: <TestDataIcon />,
            },
        ];
    }

    render() {
        return (
            <RuleEditorDiv>
                <ObserveState
                    listenTo={this.editContext}
                    properties={["title"]}
                    control={() => {
                        return (
                            <Topbar
                                onChangeName={this.onChangeRuleName}
                                editContext={this.editContext}
                                name={this.editContext.title}
                                isActionDisabled={!this.editContext.lastSelectionElement}
                                isRequiredActionGroup={false}
                                onSave={this.editContext.onSave}
                                onClose={this.editContext.onCancel}
                            />
                        );
                    }}
                />
                <ObserveState
                    listenTo={this.editContext}
                    properties={["mode"]}
                    control={() => (
                        <Sidebar
                            items={this.tabs}
                            value={this.editContext.mode}
                            onChange={(mode: string) => {
                                Reflect.set(this.editContext, "mode", mode);
                                StateManager.propertyChanged(this.editContext, "mode");
                            }}
                        />
                    )}
                />
            </RuleEditorDiv>
        );
    }
}
